import type { IPromise, IScope, translate } from 'angular';
import type { IToasterService } from 'ngtoaster';
import type { ILoginController } from './login.controller';
import { MODAL_STEP_CHANGED } from '../../../_common/modal/constant/modalConstant';

export interface ILoginStep<Status extends string> {
    title: () => string;
    description?: () => string | undefined;
    back?: Status;
    showAuthMethods?: boolean;
}

export type LoginSteps<Status extends string> = Record<Status, ILoginStep<Status>>;

export interface ILoginStatus<Status extends string> {
    status: Status;
    redirectTo?: string;
    parentCtrl?: ILoginController;

    changeStatus(status: Status): void;

    getBackStatus(status: Status): Status | undefined;

    setLoaded(loaded: boolean): void;

    setTitle(title: string): void;

    setDescription(description: string | undefined): void;

    setShowBack(showBack: boolean): void;

    changeShowRoutes(show: boolean): void;
}

export default abstract class LoginStatusController<Status extends string> implements ILoginStatus<Status> {
    abstract status: Status;
    redirectTo?: string;
    parentCtrl?: ILoginController;

    protected abstract readonly steps: LoginSteps<Status>;
    protected abstract readonly backEventName: string;
    protected abstract readonly returnErrorKey: string;

    protected constructor(
        protected readonly $scope: IScope,
        protected readonly toaster: IToasterService,
        protected readonly $translate: translate.ITranslateService,
    ) {}

    $onInit() {
        this.setLoaded(true);
        this.applyStatusView();
        this.subscribeToBackButton();

        const loading = this.loadStep();

        if (loading == null) {
            this.setLoaded(false);
            return;
        }

        void loading.finally(() => {
            this.setLoaded(false);
        });
    }

    protected loadStep(): IPromise<unknown> | undefined {
        return undefined;
    }

    changeStatus(status: Status) {
        this.setLoaded(true);

        if (!this.hasStep(status)) {
            this.toaster.pop('error', '', this.$translate.instant(this.returnErrorKey));
            this.setLoaded(false);
            return;
        }

        const stepChanged = this.status !== status;

        this.status = status;
        this.applyStatusView();
        this.setLoaded(false);

        if (stepChanged) {
            this.$scope.$emit(MODAL_STEP_CHANGED);
        }
    }

    applyStatusView() {
        if (!this.hasStep(this.status)) {
            this.toaster.pop('error', '', this.$translate.instant(this.returnErrorKey));
            return;
        }

        const step = this.steps[this.status];

        this.changeShowRoutes(step.showAuthMethods === true);
        this.setShowBack(step.back != null);
        this.setTitle(step.title());
        this.setDescription(step.description?.());
    }

    private hasStep(status: Status): boolean {
        return status != null && Object.hasOwn(this.steps, status);
    }

    getBackStatus(status: Status): Status | undefined {
        return this.steps[status]?.back;
    }

    setTitle(title: string) {
        if (this.parentCtrl == null) return;

        this.parentCtrl.title = this.parentCtrl.renderFrom === 'checkout' ? '' : title;
    }

    setDescription(description: string | undefined) {
        if (this.parentCtrl == null) return;

        this.parentCtrl.description = description;
    }

    setLoaded(loaded: boolean) {
        if (this.parentCtrl == null) return;

        this.parentCtrl.loaded = loaded;
    }

    setShowBack(showBack: boolean) {
        if (this.parentCtrl == null) return;

        this.parentCtrl.showBack = this.parentCtrl.renderFrom === 'checkout' ? false : showBack;
    }

    changeShowRoutes(show: boolean) {
        if (this.parentCtrl == null) return;

        this.parentCtrl.showAuthMethods = show;
    }

    private subscribeToBackButton() {
        this.$scope.$on(this.backEventName, () => {
            const backStatus = this.getBackStatus(this.status);

            if (backStatus == null) return;

            this.changeStatus(backStatus);
        });
    }
}
